import {
  Button,
  Checkbox,
  Loader,
  Paragraph,
  Title,
  useInert,
} from '@infotech-soe/erp-ui-kit';
import React, { FormEvent, useRef } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';

import { baseAuthFormMessages } from './messages';
import { useBaseAuthFormStyles } from './styles';

interface BaseFormProps {
  children: React.ReactNode;
  onSubmit: () => void;
  hasError?: boolean;
  isDarkMode?: boolean;
  loading?: boolean;
  logoSrc?: string;
  needChange?: boolean;
  submitLabel?: string;
  textSubtitle?: MessageDescriptor;
  valid?: boolean;
  withChange?: boolean;
  withTitle?: boolean;
  onToggle?: (isActive: boolean) => void;
}

const BaseAuthForm: React.FC<BaseFormProps> = ({
  children,
  onSubmit,
  hasError,
  isDarkMode = false,
  loading = false,
  logoSrc,
  needChange,
  submitLabel,
  textSubtitle,
  valid,
  withChange = true,
  withTitle = true,
  onToggle,
}) => {
  const classes = useBaseAuthFormStyles(isDarkMode);
  const { formatMessage } = useIntl();

  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  useInert(formRef.current, loading);

  const isSvgLogo = !!logoSrc && logoSrc.toLowerCase().endsWith('.svg');

  return (
    <form className={classes.form} ref={formRef} onSubmit={handleSubmit}>
      {loading && <Loader type="circular" className={classes.loader} />}
      <div className={classes.header}>
        {logoSrc &&
          (isSvgLogo ? (
            <div
              className={classes.logo}
              style={{
                WebkitMask: `url(${logoSrc}) no-repeat center`,
                mask: `url(${logoSrc}) no-repeat center`,
              }}
            />
          ) : (
            <img
              src={logoSrc}
              className={classes.logoImg}
              alt=""
              aria-hidden="true"
            />
          ))}
        <div className={classes.headerText}>
          {withTitle && (
            <Title className={classes.title} level={2}>
              {formatMessage(baseAuthFormMessages.title)}
            </Title>
          )}
          {textSubtitle && (
            <Paragraph className={classes.subtitle}>
              {formatMessage(textSubtitle)}
            </Paragraph>
          )}
        </div>
      </div>
      {hasError && (
        <Paragraph className={classes.error}>
          {formatMessage(baseAuthFormMessages.loginError)}
        </Paragraph>
      )}
      {children}
      <div className="row justify-between m-pt-lg">
        <Button
          className={classes.button}
          disabled={!valid}
          label={submitLabel ?? formatMessage(baseAuthFormMessages.submit)}
          type="submit"
        />
        {withChange && (
          <Checkbox
            name="changePassword"
            label={formatMessage(baseAuthFormMessages.changePassword)}
            value={needChange}
            onChange={onToggle}
          />
        )}
      </div>
    </form>
  );
};

export default BaseAuthForm;
